import { Link } from "react-router-dom";
import { imageUrl } from "@/lib/perspective";

// A small renderer for the Portable Text that Perspective posts use: paragraphs,
// headings, pull quotes, bullet and numbered lists, bold, italic, links, images and
// the "Key Takeaways" block. Kept local instead of adding a dependency.

const LinkMark = ({ href, children }) => {
    if (href?.startsWith("/")) {
        return (
            <Link to={href} className="font-medium text-brand-blue underline underline-offset-4">
                {children}
            </Link>
        );
    }
    const external = /^https?:\/\//i.test(href || "");
    return (
        <a
            href={href}
            className="font-medium text-brand-blue underline underline-offset-4"
            {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
            {children}
        </a>
    );
};

const renderSpans = (block) =>
    (block.children || []).map((span) => {
        let node = span.text;
        for (const mark of span.marks || []) {
            if (mark === "strong") node = <strong className="font-semibold text-brand-text">{node}</strong>;
            else if (mark === "em") node = <em>{node}</em>;
            else {
                const def = (block.markDefs || []).find((d) => d._key === mark);
                if (def?._type === "link") node = <LinkMark href={def.href}>{node}</LinkMark>;
            }
        }
        return <span key={span._key}>{node}</span>;
    });

const Block = ({ block, compact }) => {
    const text = renderSpans(block);
    switch (block.style) {
        case "h2":
            return (
                <h2 className="mt-12 font-heading text-2xl font-bold tracking-tight text-brand-text sm:text-3xl">
                    {text}
                </h2>
            );
        case "h3":
            return <h3 className="mt-8 font-heading text-xl font-bold tracking-tight text-brand-text">{text}</h3>;
        case "blockquote":
            return (
                <blockquote className="my-10 border-y border-brand-line py-8 font-heading text-2xl font-bold leading-snug tracking-tight text-brand-text sm:text-3xl">
                    {text}
                </blockquote>
            );
        default:
            return <p className={compact ? "mt-3 first:mt-0" : "mt-6 first:mt-0"}>{text}</p>;
    }
};

const List = ({ type, items, compact }) => {
    const Tag = type === "number" ? "ol" : "ul";
    return (
        <Tag
            className={`${compact ? "mt-3" : "mt-6"} space-y-2 pl-6 ${
                type === "number" ? "list-decimal" : "list-disc marker:text-brand-accent"
            }`}
        >
            {items.map((item) => (
                <li key={item._key} className="pl-1">
                    {renderSpans(item)}
                </li>
            ))}
        </Tag>
    );
};

const KeyTakeaways = ({ value }) => (
    <aside className="my-10 rounded-3xl border border-brand-line bg-brand-surface p-7 sm:p-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-accent">
            {value.heading || "Key Takeaways"}
        </p>
        <ul className="mt-4 list-disc space-y-2 pl-6 marker:text-brand-accent">
            {(value.items || []).map((item, i) => (
                <li key={i}>{item}</li>
            ))}
        </ul>
    </aside>
);

const Figure = ({ value }) => {
    const src = value?.asset?.url || value?.url;
    if (!src) return null;
    return (
        <figure className="my-10">
            <img
                src={imageUrl(src, 1400)}
                alt={value.alt || ""}
                loading="lazy"
                className="w-full rounded-3xl border border-brand-line"
            />
            {value.caption && (
                <figcaption className="mt-3 text-sm text-brand-muted">{value.caption}</figcaption>
            )}
        </figure>
    );
};

// Group consecutive list items into one list, as Portable Text stores them flat.
const groupBlocks = (blocks = []) => {
    const out = [];
    for (const block of blocks) {
        const last = out[out.length - 1];
        if (block._type === "block" && block.listItem) {
            if (last?.kind === "list" && last.type === block.listItem) last.items.push(block);
            else out.push({ kind: "list", type: block.listItem, items: [block], _key: block._key });
        } else {
            out.push({ kind: "node", value: block, _key: block._key });
        }
    }
    return out;
};

export const PortableText = ({ value, compact = false }) => (
    <>
        {groupBlocks(value).map((node) => {
            if (node.kind === "list") return <List key={node._key} type={node.type} items={node.items} compact={compact} />;
            const v = node.value;
            if (v._type === "block") return <Block key={node._key} block={v} compact={compact} />;
            if (v._type === "keyTakeaways") return <KeyTakeaways key={node._key} value={v} />;
            if (v._type === "image") return <Figure key={node._key} value={v} />;
            return null;
        })}
    </>
);
